import React, { useState, useEffect, useMemo } from 'react';
import PaginationControls from '../../components/common/PaginationControls';
import AdminSidebar from '../../components/admin/AdminSidebar';
import NoticeToast from '../../components/common/NoticeToast';
import { apiUrl } from '../../config/api';
import { apiFetch } from '../../api/client';
import { useNotice } from '../../hooks/useNotice';
import './SessionsManagement.css';

const API_BASE = apiUrl('/api/admin/sessions');
const ITEMS_PER_PAGE = 5;

export default function SessionsManagement() {
  const { notice, showNotice, clearNotice } = useNotice();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    code: '',
    nom: '',
    date_debut: '',
    date_fin: '',
    actif: true
  });

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(API_BASE, { auth: true });
      if (!response.ok) throw new Error('Erreur lors du chargement');
      const data = await response.json();
      setSessions(Array.isArray(data) ? data : (data.sessions || []));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const url = editingId ? `${API_BASE}/${editingId}` : API_BASE;
      const method = editingId ? 'PUT' : 'POST';

      const response = await apiFetch(url, {
        method,
        auth: true,
        json: true,
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Erreur lors de la sauvegarde');
      }

      await loadSessions();
      resetForm();
      showNotice(editingId ? 'Session modifiee avec succes.' : 'Session ajoutee avec succes.', 'success');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (session) => {
    setFormData({
      code: session.code,
      nom: session.nom,
      date_debut: session.date_debut || '',
      date_fin: session.date_fin || '',
      actif: session.actif
    });
    setEditingId(session.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette session ?')) return;

    try {
      const response = await apiFetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
        auth: true,
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Erreur lors de la suppression');
      }
      await loadSessions();
      showNotice('Session supprimee avec succes.', 'success');
    } catch (err) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({ code: '', nom: '', date_debut: '', date_fin: '', actif: true });
    setEditingId(null);
    setShowForm(false);
  };

  const sortedSessions = useMemo(() => {
    return [...sessions].sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      if (dateB !== dateA) {
        return dateB - dateA;
      }
      return b.id - a.id;
    });
  }, [sessions]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(sortedSessions.length / ITEMS_PER_PAGE));
  }, [sortedSessions.length]);

  const paginatedSessions = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedSessions.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedSessions, currentPage]);

  const sessionKpis = useMemo(() => {
    const activeCount = sessions.filter((session) => Boolean(session.actif)).length;
    const inactiveCount = sessions.filter((session) => !session.actif).length;
    const datedCount = sessions.filter((session) => session.date_debut && session.date_fin).length;

    return [
      { label: 'Total sessions', value: sessions.length, helper: 'référentiel complet' },
      { label: 'Sessions actives', value: activeCount, helper: 'utilisables actuellement' },
      { label: 'Sessions inactives', value: inactiveCount, helper: 'désactivées' },
      { label: 'Dates configurées', value: datedCount, helper: 'début et fin saisis' },
    ];
  }, [sessions]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  if (loading) {
    return (
      <div className="admin-shell">
        <AdminSidebar />
        <div className="admin-content">
          <div className="sessions-management">
            <div className="loading-spinner">Chargement des sessions...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <div className="admin-content">
        <div className="sessions-management">
          <div className="sessions-header">
            <h2>Gestion des sessions</h2>
            <div className="sessions-header-info">
              <span>{sessions.length} session(s)</span>
            </div>
            <div className="header-actions">
              <button
                className="btn-add"
                onClick={() => setShowForm(!showForm)}
              >
                {showForm ? '✕ Annuler' : '+ Ajouter'}
              </button>
            </div>
          </div>

          <div className="sessions-kpi-strip">
            {sessionKpis.map((kpi) => (
              <div key={kpi.label} className="sessions-kpi-card">
                <span className="sessions-kpi-label">{kpi.label}</span>
                <strong className="sessions-kpi-value">{kpi.value}</strong>
                <span className="sessions-kpi-helper">{kpi.helper}</span>
              </div>
            ))}
          </div>

          {error && <div className="error-banner">{error}</div>}

          {showForm && (
            <form className="session-form card" onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Code de session *</label>
                  <input
                    type="text"
                    name="code"
                    placeholder="Ex: H2026, A2025"
                    value={formData.code}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Nom descriptif *</label>
                  <input
                    type="text"
                    name="nom"
                    placeholder="Ex: Hiver 2026"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Date de début</label>
                  <input
                    type="date"
                    name="date_debut"
                    value={formData.date_debut}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Date de fin</label>
                  <input
                    type="date"
                    name="date_fin"
                    value={formData.date_fin}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="actif"
                      checked={formData.actif}
                      onChange={handleChange}
                    />
                    Session active
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">
                  {editingId ? '💾 Mettre à jour' : '➕ Ajouter'}
                </button>
                <button type="button" className="btn-cancel" onClick={resetForm}>
                  ✕ Annuler
                </button>
              </div>
            </form>
          )}

          <div className="sessions-table-wrapper card">
            {sessions.length === 0 ? (
              <p className="no-data">Aucune session pour le moment. Ajoutez-en une pour commencer.</p>
            ) : (
              <>
                <table className="sessions-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Nom</th>
                      <th>Dates</th>
                      <th>Statut</th>
                      <th>Créée le</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSessions.map(session => (
                      <tr key={session.id}>
                        <td className="code">{session.code}</td>
                        <td className="nom">{session.nom}</td>
                        <td className="dates">
                          {session.date_debut && session.date_fin ? (
                            `${session.date_debut.slice(0, 10)} → ${session.date_fin.slice(0, 10)}`
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="statut">
                          <span className={`badge ${session.actif ? 'active' : 'inactive'}`}>
                            {session.actif ? '✓ Actif' : '✗ Inactif'}
                          </span>
                        </td>
                        <td className="created">
                          {session.created_at ? new Date(session.created_at).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td className="actions">
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(session)}
                            title="Modifier"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(session.id)}
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </>
            )}
        </div>
        </div>
        <NoticeToast notice={notice} onClose={clearNotice} />
      </div>
    </div>
  );
}
